/**
 * nj-flow-migrate.ts — read-time migration for journey documents stored before
 * the document held a flow TREE (2026-08-31).
 *
 * This replaces nj-flow-adapter.ts, which was a two-way seam: the document kept
 * three flat step lists (`flowMain` + `flowYes`/`flowNo`), the builder wanted a
 * tree, and every edit round-tripped through a conversion that could hold only
 * ONE decision — a second decision's branches were flattened into the trunk, and
 * a third path had nowhere to go at all. The document now stores the tree
 * (`Doc.flow`) and the builder reads it directly, so there is nothing to
 * round-trip and this file only ever runs ONE WAY, ONCE, on load.
 *
 * The old document's steps carried their meaning as English prose in a `title`
 * ("Wait until 24 h before"), which is why parseWait and canonOutcome exist:
 * they recover structure from that prose rather than defaulting and silently
 * contradicting the step after it. Newly written flows never need them.
 */
import {
  qfVocabFor, qfMigrate,
  type QFFlow, type QFStep, type QFVocab,
} from "./jf-model";

let seq = 0;
const nid = (p: string) => `${p}_${Date.now().toString(36)}${(seq++).toString(36)}`;

/** The legacy shape, as it comes off the wire. Deliberately loose. */
interface LegacyStep {
  id?: string; kind?: string; title?: string; sub?: string; page?: number;
  chips?: string[]; promo?: boolean;
  messageId?: string | null; surveyId?: string | null;
  event?: string; amount?: number; unit?: string; timing?: string;
  condition?: string; outcome?: string; sendWhen?: string;
}
export interface LegacyFlowDoc {
  type?: string;
  flow?: QFFlow;
  flowMain?: LegacyStep[];
  flowYes?: LegacyStep[];
  flowNo?: LegacyStep[];
}

/** "Wait until 24 h before" → { amount: 24, unit: "Hours", timing: … }. */
export function parseWait(title: string | undefined, vocab: QFVocab) {
  const timings = vocab.waitTimings.map((t) => t.value);
  const has = (v: string) => (timings.indexOf(v) > -1 ? v : timings[0]);
  const t = String(title ?? "").toLowerCase();
  const num = t.match(/(\d+)/);
  const unit = /\b(h|hr|hrs|hour|hours)\b/.test(t) ? "Hours" : /\b(d|day|days)\b/.test(t) ? "Days" : "Minutes";
  let timing = timings[0];
  if (/before the event/.test(t)) timing = has("before_event");
  else if (/after the event/.test(t)) timing = has("after_event");
  else if (/before/.test(t)) timing = has("before_appt") !== timings[0] ? has("before_appt") : has("before_event");
  else if (/service ends|after the service/.test(t)) timing = has("after_service");
  else if (/called|call/.test(t)) timing = has("after_called");
  return { amount: num ? Number(num[1]) : 5, unit, timing };
}

/** Land a legacy free-text outcome on this type's vocabulary. */
export function canonOutcome(raw: string | undefined, vocab: QFVocab): string {
  const v = String(raw ?? "").trim();
  const outs = vocab.exitOutcomes;
  if (outs.indexOf(v) > -1) return v;
  const lc = v.toLowerCase();
  const near = outs.filter((o) => { const ol = o.toLowerCase(); return lc.indexOf(ol) > -1 || ol.indexOf(lc) > -1; })[0];
  if (near) return near;
  if (/left|abandon/.test(lc)) return outs.filter((o) => /left/i.test(o))[0] ?? outs[0];
  if (/no.?show|missed/.test(lc)) return outs.filter((o) => /no.?show/i.test(o))[0] ?? outs[0];
  if (/complete|served|attend|book|regist/.test(lc)) return outs[0];
  return v || outs[0];
}

/**
 * The document's flow, whatever shape it was stored in. A document already
 * carrying `flow` is passed through qfMigrate (which upgrades any decision still
 * holding `{ yes, no }`); one carrying the three flat lists is converted.
 * Idempotent, so it is safe on every load.
 */
export function njFlowOf(doc: LegacyFlowDoc): QFFlow {
  if (doc?.flow && Array.isArray(doc.flow.steps)) return qfMigrate(doc.flow);

  const vocab = qfVocabFor(doc?.type);
  /* The old model could hold exactly one decision, and its branches lived in
     these two lists — so the FIRST decision found claims them. */
  let branchTaken = false;
  const conv = (list: LegacyStep[] | undefined): QFStep[] => {
    const out: QFStep[] = [];
    (list ?? []).forEach((st) => {
      const id = st.id || nid("s");
      switch (st.kind) {
        /* The old top-of-flow trigger was never a canvas node: the flow starts
           at the first customer screen (operator direction 2026-07-21). */
        case "trigger": return;
        /* Question groups belong to the page that asks them. */
        case "questions": return;
        case "qtrigger":
          out.push({ id, kind: "qtrigger", event: st.event ?? vocab.flowTriggers[0].id });
          return;
        case "page":
          out.push({ id, kind: "page", page: String(st.page ?? 0), cfg: {} });
          return;
        case "comms":
          out.push({
            id, kind: "comms", messageId: st.messageId ?? null,
            sendWhen: st.sendWhen ?? "Immediately", landingId: "",
            ...(st.title ? { hint: st.title } : {}),
            ...(st.promo ? { promo: true } : {}),
          });
          return;
        case "survey":
          out.push({
            id, kind: "survey", surveyId: st.surveyId ?? null,
            sendWhen: st.sendWhen ?? "Immediately",
            ...(st.title ? { hint: st.title } : {}),
          });
          return;
        case "wait": {
          /* Structured fields win; parseWait only rescues legacy prose titles. */
          const w = st.timing && st.unit && st.amount != null
            ? { amount: st.amount, unit: st.unit, timing: st.timing }
            : parseWait(st.title, vocab);
          out.push({ id, kind: "wait", ...w });
          return;
        }
        case "decision": {
          const claim = !branchTaken;
          branchTaken = true;
          out.push({
            id, kind: "decision",
            condition: st.condition ?? st.title ?? vocab.decisionConds[0],
            paths: [
              { key: "yes", label: "Yes", steps: claim ? conv(doc.flowYes) : [] },
              { key: "no", label: "No", steps: claim ? conv(doc.flowNo) : [] },
            ],
          });
          return;
        }
        case "exit":
          out.push({ id, kind: "exit", outcome: canonOutcome(st.outcome ?? st.title, vocab), note: st.sub ?? "" });
          return;
        default: return;
      }
    });
    return out;
  };
  return { trigger: { event: "screen_services" }, steps: conv(doc?.flowMain) };
}
